import { bool, func, node, oneOf, string, number } from 'prop-types';
import React from 'react';
import uniqueId from 'lodash.uniqueid';
import FocusLock from 'react-focus-lock';
import Backdrop from '@ids/backdrop';
import IDSContext, { zIndexManager } from '@ids/context';
import Portal from '@ids/portal';
import { themes, ThemeContext, getTheme } from '@ids/theme';
import Transition, { transitions } from '@ids/transition';
import ZIndex from '@ids/zindex';
import { styleOverrideReason } from '@ids/core';
import { StyledCloseIcon, Container, Wrapper } from './StyledModalDialog';

/** */
export default class Modal extends React.Component {
  static contextType = ThemeContext;

  static propTypes = {
    /**
     * Component ID
     */
    automationId: string,
    /**
     * Inner content of modal dialog
     */
    children: node,
    /**
     * Theme of close icon control - default is light
     */
    // eslint-disable-next-line react/boolean-prop-naming
    closeIconInverse: bool,
    /**
     * Shows close icon which allows the modal dialog to be dismissed
     */
    // eslint-disable-next-line react/boolean-prop-naming
    dismissible: bool,
    /**
     * Changes backdrop to light theme
     */
    // eslint-disable-next-line react/boolean-prop-naming
    lightBackdrop: bool,
    /**
     * Callback when the modal dialog is closed
     */
    onClose: func,
    /**
     * Callback when the modal dialog is opened
     */
    onShow: func,
    /**
     * Determines whether modal dialog is opened or closed
     */
    // eslint-disable-next-line react/boolean-prop-naming
    open: bool,
    /**
     * Size of modal dialog
     */
    size: oneOf(['small', 'medium', 'large']),
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason,
    /**
     * When there is an application level theme defined, use this prop to override
     * (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: oneOf(themes),
    /**
     * Deprecated: Property to set zIndex for Modal and Backdrop. If provided zIndex would be adjusted
     * by the component.
     */
    backdropZIndex: number,
    /**
     * Flag to indicate if Modal component consumer wants to handle zIndex by self.
     * Default is set to false to let ZIndex component handle it.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableZIndex: bool,
    /**
     * Flag to indicate if restore focus is disabled
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableRestoreFocus: bool
  };

  static defaultProps = {
    automationId: 'ModalDialog',
    children: undefined,
    closeIconInverse: false,
    dismissible: false,
    lightBackdrop: false,
    onClose: () => {},
    onShow: () => {},
    open: true,
    size: 'medium',
    styleOverrideReason: undefined,
    theme: undefined,
    backdropZIndex: undefined,
    disableZIndex: false,
    disableRestoreFocus: false
  };

  constructor(props) {
    super(props);
    // element that triggered modal launch
    this.lastActive = null;
    this.modalId = uniqueId('idsModalHeader');

    this.modalBackgroundRef = React.createRef();
    this.modalContentRef = React.createRef();
    if (!props.backdropZIndex) {
      zIndexManager.incrementBaseZIndex();
      this.zIndexForBackdrop = zIndexManager.getCurrentBaseZIndex();
    }
  }

  componentDidMount() {
    if (this.props.open) {
      this.handleOpen();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.lastActive = document.activeElement;
      this.handleOpen();
    }
  }

  onBackgroundClick = event => {
    if (event.target === this.modalBackgroundRef) {
      this.props.onClose();
    }
  };

  // handles focus back to trigger element
  handleFocusBack = () => {
    if (this.lastActive !== null && this.lastActive.focus) {
      this.lastActive.focus();
    }
  };

  handleOpen() {
    this.props.onShow();
  }

  handleKeyDown = event => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      this.props.onClose();
    }
  };

  renderBackdrop = () => {
    const zIndex = this.props.backdropZIndex
      ? this.props.backdropZIndex
      : this.zIndexForBackdrop;
    return (
      <Portal>
        <Backdrop open light={this.props.lightBackdrop} zIndex={zIndex} />
      </Portal>
    );
  };

  renderModal() {
    let modalContainerZIndex;
    let modalContentZIndex;
    const {
      automationId,
      closeIconInverse,
      disableRestoreFocus,
      dismissible,
      onClose,
      onShow,
      open,
      size,
      disableZIndex,
      backdropZIndex,
      // eslint-disable-next-line react/prop-types
      style,
      ...restProps
    } = this.props;

    if (backdropZIndex) {
      modalContainerZIndex = backdropZIndex + 1;
      modalContentZIndex = backdropZIndex + 2;
      console.warn(
        'Deprecated backdropZIndex: Refrain from using this prop as it is soon going to be removed from later versions of component.'
      );
    }

    const { currentTheme } = getTheme(this.props, this.context);

    const modalElement = (
      <Transition
        unmountOnExit
        transition={open ? transitions.enter : transitions.exit}
        onUnmount={disableRestoreFocus ? null : this.handleFocusBack}
      >
        <Container
          ref={n => {
            this.modalBackgroundRef = n;
          }}
          data-automation-id={`${automationId}--container`}
          open={open}
          role="dialog"
          style={{ zIndex: modalContainerZIndex }}
          onClick={this.onBackgroundClick}
          {...restProps}
        >
          <FocusLock>
            <Wrapper
              ref={n => {
                this.modalContentRef = n;
              }}
              aria-labelledby={this.modalId}
              data-automation-id={automationId}
              open={open}
              size={size}
              style={{ ...style, zIndex: modalContentZIndex }}
              tabIndex={0}
              theme={currentTheme}
              onKeyDown={this.handleKeyDown}
              {...restProps}
            >
              <div>
                {dismissible && (
                  <StyledCloseIcon
                    inverse={closeIconInverse}
                    size="medium"
                    onClick={onClose}
                  />
                )}
                {this.renderChildren()}
              </div>
            </Wrapper>
          </FocusLock>
        </Container>
      </Transition>
    );

    return (
      <Portal>
        {backdropZIndex === undefined || backdropZIndex === null ? (
          <IDSContext.Consumer>
            {context => (
              <ZIndex
                assignZIndex={open}
                getCurrentBaseZIndex={context.getCurrentBaseZIndex}
                incrementBaseZIndex={context.incrementBaseZIndex}
                decrementBaseZIndex={context.decrementBaseZIndex}
                disableZIndex={disableZIndex}
              >
                {open ? modalElement : <div />}
              </ZIndex>
            )}
          </IDSContext.Consumer>
        ) : (
          modalElement
        )}
      </Portal>
    );
  }

  renderChildren() {
    // Check to make sure children are not null
    const filteredChildren = React.Children.toArray(this.props.children).filter(
      child => child !== null
    );

    return React.Children.map(filteredChildren, child => {
      switch (child.type.displayName) {
        case 'ModalHeader':
          return React.cloneElement(child, {
            id: this.modalId
          });
        default:
          return child;
      }
    });
  }

  render() {
    return (
      <div>
        {this.props.open && this.renderBackdrop()}
        {this.renderModal()}
      </div>
    );
  }
}
